import { useCallback, useEffect, useRef } from "react";
import { Star, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { GOOGLE_REVIEWS_URL, REVIEWS, IMAGES } from "../data/content";
import { Reveal } from "../components/Reveal";

const GoogleMark = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z" />
    <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.88 8.87 4.77 12 4.77z" />
  </svg>
);

const Stars = ({ n }) => (
  <div className="flex gap-0.5" aria-label={`${n} de 5 estrellas`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`h-3.5 w-3.5 ${i < n ? "fill-[#FBBC05] text-[#FBBC05]" : "text-zinc-700"}`} />
    ))}
  </div>
);

const ReviewCard = ({ r, i }) => (
  <article
    data-testid={`rnb-review-card-${i + 1}`}
    className="min-w-0 shrink-0 grow-0 basis-[82%] sm:basis-[46%] lg:basis-[31.5%] snap-start border border-zinc-800 bg-[#0D0D0D] p-6 sm:p-7 flex flex-col"
  >
    <header className="flex items-center gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-white truncate">{r.name}</p>
        <p className="text-[11px] text-zinc-500 truncate">{r.meta}</p>
      </div>
      <GoogleMark className="h-4 w-4 ml-auto shrink-0 opacity-80" />
    </header>
    <div className="mt-4 flex items-center gap-2.5">
      <Stars n={r.stars} />
      <span className="text-[11px] text-zinc-500">{r.time}</span>
    </div>
    <p className="mt-4 text-sm text-zinc-300 font-light leading-relaxed whitespace-pre-line">{r.text}</p>
    {r.photo && (
      <img
        src={r.photo}
        alt={`Foto publicada por ${r.name} en su reseña`}
        loading="lazy"
        draggable={false}
        className="mt-5 w-full h-auto border border-zinc-800 pointer-events-none"
      />
    )}
  </article>
);

const Reviews = () => {
  const trackRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });
  const hovering = useRef(false);

  const cardStep = useCallback(() => {
    const track = trackRef.current;
    if (!track) return 380;
    const card = track.querySelector("article");
    return card ? card.offsetWidth + 20 : 380;
  }, []);

  const prev = useCallback(() => trackRef.current?.scrollBy({ left: -cardStep(), behavior: "smooth" }), [cardStep]);
  const next = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 10;
    if (atEnd) track.scrollTo({ left: 0, behavior: "smooth" });
    else track.scrollBy({ left: cardStep(), behavior: "smooth" });
  }, [cardStep]);

  useEffect(() => {
    const id = setInterval(() => {
      if (!drag.current.down && !hovering.current) next();
    }, 6000);
    return () => clearInterval(id);
  }, [next]);

  const onPointerDown = (e) => {
    if (e.pointerType !== "mouse") return;
    const track = trackRef.current;
    if (!track) return;
    e.preventDefault();
    const startX = e.clientX;
    const startScroll = track.scrollLeft;
    track.style.scrollSnapType = "none";
    hovering.current = true;
    drag.current.moved = false;
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      if (Math.abs(dx) > 6) drag.current.moved = true;
      track.scrollLeft = startScroll - dx;
    };
    const onUp = () => {
      track.style.scrollSnapType = "";
      hovering.current = false;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return (
    <section id="resenas" data-testid="rnb-reviews-section" className="relative py-20 lg:py-32 bg-[#080808] border-y border-zinc-900 overflow-hidden">
      <img src={IMAGES.editorial.bgTraining} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-cover object-[center_30%]" />
      <div className="absolute inset-0 bg-black/70" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#080808] via-transparent to-[#080808]" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className="text-xs tracking-[0.4em] text-zinc-500 uppercase">Testimonios · Google</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
              LO QUE DICE EL <span className="text-transparent" style={{ WebkitTextStroke: "1.5px #fff" }}>TEAM RNB</span>
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rnb-reviews-google-link"
              className="group inline-flex items-center gap-2.5 border border-zinc-700 px-5 py-3 text-[11px] tracking-[0.2em] uppercase text-zinc-300 hover:bg-white hover:text-black hover:border-white transition-colors"
            >
              <GoogleMark className="h-4 w-4" />
              Ver todas en Google
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <div className="flex items-center gap-3">
              <button onClick={prev} data-testid="rnb-reviews-prev" aria-label="Reseña anterior" className="h-12 w-12 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:bg-white hover:text-black hover:border-white transition-colors">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button onClick={next} data-testid="rnb-reviews-next" aria-label="Reseña siguiente" className="h-12 w-12 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:bg-white hover:text-black hover:border-white transition-colors">
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-12">
          <div
            ref={trackRef}
            data-testid="rnb-reviews-track"
            data-lenis-prevent
            onPointerDown={onPointerDown}
            onClickCapture={onClickCapture}
            onMouseEnter={() => (hovering.current = true)}
            onMouseLeave={() => (hovering.current = false)}
            className="no-scrollbar flex gap-4 sm:gap-5 overflow-x-auto snap-x snap-mandatory items-start pl-0 pr-4 sm:pr-6 lg:pr-8 cursor-grab active:cursor-grabbing select-none"
            style={{ touchAction: "pan-y pinch-zoom" }}
          >
            {REVIEWS.map((r, i) => (
              <ReviewCard key={r.name} r={r} i={i} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Reviews;
